import { FrameworkHierarchy } from "@/contexts/entities/EntitiesContext";
import { Project } from "@/contexts/projects/ProjectsContext";
import { EventType, handleEvent } from "./base";
import { ClickActions } from "./navigation";
import { Activity, SubCompetency } from "../models";

export enum SavedResourcesLocation {
  Drawer = "drawer",
  SavedResourcesActionBar = "saved-resources-action-bar",
  OverviewSkillModal = "overview-skill-modal",
  SubCompetencyList = "sub-competency-list",
}

export const handleAddActivityToSavedResourcesEvent = (
  slug: string,
  name: string,
) => {
  handleEvent(EventType.SavedResources, {
    slug,
    name,
    action: "add_to_saved_resources",
  });
};

export const handleRemoveActivityFromSavedResourcesEvent = (
  slug: string,
  name: string,
) => {
  handleEvent(EventType.SavedResources, {
    slug,
    name,
    action: "remove_from_saved_resources",
  });
};

export const handleAddToSavedResourcesEvent = (
  entityHierarchy: FrameworkHierarchy,
  location: SavedResourcesLocation,
) => {
  const eventProperties = getAddToSavedResourcesProperties(
    entityHierarchy,
    location,
  );
  handleEvent(EventType.SavedResources, eventProperties);
};

export const handleRemoveFromSavedResourcesEvent = (
  entityHierarchy: FrameworkHierarchy,
  location: SavedResourcesLocation,
) => {
  const eventProperties = getRemoveFromSavedResourcesProperties(
    entityHierarchy,
    location,
  );
  handleEvent(EventType.SavedResources, eventProperties);
};

export const handleBulkAddSkillsToSavedResourcesEvent = (
  subcompetencyNames: string[],
  location: SavedResourcesLocation,
) => {
  handleEvent(EventType.SavedResources, {
    action: "bulk_add_to_saved_resources",
    subcompetency: subcompetencyNames,
    location,
    target: "subcompetency",
  });
};

export const handleBulkRemoveFromSavedResourcesEvent = (
  entityHierarchy: FrameworkHierarchy[],
  location: SavedResourcesLocation,
) => {
  if (entityHierarchy.length === 1) {
    const eventProperties = getRemoveFromSavedResourcesProperties(
      entityHierarchy[0],
      location,
    );
    handleEvent(EventType.SavedResources, eventProperties);
    return;
  }
  const eventProperties = getBulkRemoveFromSavedResourcesProperties(
    entityHierarchy,
    location,
  );
  handleEvent(EventType.SavedResources, eventProperties);
};

export const handleBulkAddActivitiesToSavedResourcesEvent = (
  activities: Activity[],
  location: SavedResourcesLocation,
) => {
  handleEvent(EventType.SavedResources, {
    action: "bulk_add_to_saved_resources",
    activities: activities.map((activity) => activity.urlSlug),
    activity_title: activities.map((activity) => activity.name),
    location,
    target: "activity",
  });
};

export const handleBulkRemoveSkillsFromProjectEvent = (
  projectName: string,
  subcompetencyIds: string[],
) => {
  handleEvent(EventType.ProjectSkills, {
    action: "bulk_remove_skills_from_project",
    project_name: projectName,
    subcompetency: subcompetencyIds,
  });
};

export const handleSavedResourcesAccordionEvent = (
  id: string,
  action: ClickActions,
) => {
  handleEvent(EventType.SavedResources, {
    id,
    action,
  });
};

export const handleComponentSkillActivitySectionEvent = (
  componentSkill: SubCompetency,
  action: ClickActions,
) => {
  const activitySlugs = componentSkill.activities.flatMap((activity) => {
    return activity.urlSlug;
  });
  handleEvent(EventType.RelatedActivities, {
    activities: activitySlugs,
    component_skill_name: componentSkill.name,
    component_id: componentSkill.id,
    action,
  });
};

export const handleProjectCreationEvent = (projectData: Project) => {
  handleEvent(EventType.SavedResources, {
    action: "project_creation",
    ...projectData,
  });
};

export const handleProjectAdditionEvent = (
  projectId: string,
  componentSkills: string[],
) => {
  handleEvent(EventType.SavedResources, {
    action: "project_addition",
    projectId,
    componentSkills,
  });
};

export const getAddToSavedResourcesProperties = (
  entityHierarchy: FrameworkHierarchy,
  location: SavedResourcesLocation,
) => {
  const action = "add_to_saved_resources";
  if (entityHierarchy.activity) {
    return {
      competency: entityHierarchy.competency?.name,
      activity: entityHierarchy.activity.urlSlug,
      activity_title: entityHierarchy.activity.name,
      action,
      location,
      learner_outcome: entityHierarchy.learnerOutcome?.name,
      target: "activity",
    };
  }
  return {
    subcompetency: entityHierarchy.subCompetency?.name,
    competency: entityHierarchy.competency?.name,
    learner_outcome: entityHierarchy.learnerOutcome?.name,
    action,
    location,
    target: "subcompetency",
  };
};

export const getBulkRemoveFromSavedResourcesProperties = (
  entityHierarchy: FrameworkHierarchy[],
  location: SavedResourcesLocation,
) => {
  const action = "bulk_remove_from_saved_resources";
  if (entityHierarchy[0].activity) {
    return {
      action,
      activities: entityHierarchy.map(
        (hierarchy) => hierarchy.activity?.urlSlug,
      ),
      activity_title: entityHierarchy.map(
        (hierarchy) => hierarchy.activity?.name,
      ),
      location,
      target: "activity",
    };
  }
  return {
    action,
    subcompetency: entityHierarchy.map(
      (hierarchy) => hierarchy.subCompetency?.name,
    ),
    location,
    target: "subcompetency",
  };
};

export const getRemoveFromSavedResourcesProperties = (
  entityHierarchy: FrameworkHierarchy,
  location: SavedResourcesLocation,
) => {
  const action = "remove_from_saved_resources";
  if (entityHierarchy.activity) {
    return {
      competency: entityHierarchy.competency?.name,
      activity: entityHierarchy.activity.urlSlug,
      activity_title: entityHierarchy.activity.name,
      action,
      location,
      learner_outcome: entityHierarchy.learnerOutcome?.name,
      target: "activity",
    };
  }
  return {
    subcompetency: entityHierarchy.subCompetency?.name,
    competency: entityHierarchy.competency?.name,
    learner_outcome: entityHierarchy.learnerOutcome?.name,
    action,
    location,
    target: "subcompetency",
  };
};

export const handleProjectCardDetailClick = (projectData: Project) => {
  handleEvent(EventType.NavigationClick, {
    action: "project_card_detail_click",
    ...projectData,
  });
};
export const handleViewProjectClick = (projectData: Project) => {
  handleEvent(EventType.NavigationClick, {
    action: "view_created_project_click",
    ...projectData,
  });
};

export const handleProjectComponentSkillsAccordionEvent = (
  project_name: string,
  id: string,
  action: ClickActions,
) => {
  handleEvent(EventType.ProjectSkills, {
    project_name,
    id,
    action,
  });
};

export const handleProjectDeletionEvent = (project: Project) => {
  handleEvent(EventType.ProjectSkills, {
    action: "delete",
    name: project.name,
    description: project.description,
  });
};

export const handleProjectExportEvent = (project: Project) => {
  const eventProperties = getSetExportProperties(project);
  handleEvent(EventType.ProjectSkills, eventProperties);
};

const getSetExportProperties = (project: Project) => {
  return {
    action: "export",
    format: "csv",
    location: "project_details_page",
    component_skills_count: project.componentSkills.length,
    subcompetencies: project.componentSkills,
    project_name: project.name,
  };
};

export const handleProjectEditClick = (
  project_name: string,
  id: string,
  field: string,
) => {
  handleEvent(EventType.ProjectEdit, {
    project_name,
    id,
    field,
    action: "project_edit_click",
  });
};

export const handleProjectEditSaveClick = (
  project_name: string,
  id: string,
  field: string,
) => {
  handleEvent(EventType.ProjectEdit, {
    project_name,
    id,
    field,
    action: "project_edit_save_click",
  });
};
export const handleProjectEditCancelClick = (
  project_name: string,
  id: string,
  field: string,
) => {
  handleEvent(EventType.ProjectEdit, {
    project_name,
    id,
    field,
    action: "project_edit_cancel_click",
  });
};
